import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Spin,
  Card,
  Statistic,
  Table,
  message,
} from 'antd';
import AdminLayout from '../../../components/admin/AdminLayout';
import { adminApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import { setSubscription as persistApprovedSubscription } from '../../../modules/subscriptionStorage';
import { getPlanById } from '../../../constants/plans';
import { findPublishedPlan } from '../../../modules/publicPlanStorage';
import { formatCurrencyAmount, formatMonthlyPrice } from '../../../utils/pricing';
import './adminDashboard.css';

class AdminDashboard extends Component {
  planProductMap = new Map();

  productsCache = null;

  state = {
    loading: true,
    overview: null,
    error: null,
    subscriptionRequests: [],
    approvingRequestId: null,
  };

  componentDidMount() {
    this.fetchOverview();
    this.fetchSubscriptionRequests();
  }

  componentWillUnmount() {
  }

  async fetchOverview() {
    this.setState({ loading: true, error: null });
    try {
      const response = await adminApi.fetchDashboardOverview();
      const data = response && response.data ? response.data : response;
      this.setState({ overview: data, loading: false });
    } catch (error) {
      this.setState({
        loading: false,
        error: 'Unable to load dashboard metrics. Try again later.',
      });
    }
  }

  async fetchSubscriptionRequests() {
    try {
      const response = await adminApi.fetchSubscriptionRequests({ status: 'pending' });
      const payload = response && response.data ? response.data : {};
      this.setState({ subscriptionRequests: Array.isArray(payload.data) ? payload.data : [] });
    } catch (error) {
      this.setState({ subscriptionRequests: [] });
    }
  }

  async loadProductsCache(force = false) {
    if (!force && this.productsCache) {
      return this.productsCache;
    }
    try {
      const response = await adminApi.fetchProducts();
      const data = response && response.data ? response.data : response;
      this.productsCache = Array.isArray(data) ? data : [];
    } catch (error) {
      this.productsCache = [];
    }
    return this.productsCache;
  }

  async ensureProductForPlan(plan) {
    if (!plan) {
      throw new Error('Plan configuration is missing.');
    }

    await this.loadProductsCache();

    const planKey = plan.planId || plan.id || plan.productId;
    const candidateIds = [];
    if (plan.productId) {
      candidateIds.push(plan.productId);
    }
    if (planKey && this.planProductMap.has(planKey)) {
      candidateIds.push(this.planProductMap.get(planKey));
    }

    const findProductById = (productId) =>
      (this.productsCache || []).find((item) => (item._id || item.id) === productId) || null;

    for (const candidateId of candidateIds.filter(Boolean)) {
      const existingProduct = findProductById(candidateId);
      if (existingProduct) {
        return candidateId;
      }
    }

    if (candidateIds.length) {
      await this.loadProductsCache(true);
      for (const candidateId of candidateIds.filter(Boolean)) {
        const refreshedProduct = findProductById(candidateId);
        if (refreshedProduct) {
          return candidateId;
        }
      }
    }

    const normalizedPriceSource =
      plan.priceAmount !== undefined && plan.priceAmount !== null
        ? Number(plan.priceAmount)
        : Number(
            String(plan.price || plan.priceDisplay || '')
              .replace(/[^0-9.]/g, '')
              .trim(),
          );
    const normalizedPrice = Number.isFinite(normalizedPriceSource)
      ? Number(normalizedPriceSource.toFixed(2))
      : null;
    if (!normalizedPrice || normalizedPrice <= 0) {
      throw new Error(`Invalid price configuration for plan "${planKey || 'unknown'}".`);
    }
    const normalizedPriceFixed = normalizedPrice.toFixed(2);

    const normalizedPeriod =
      Number.isFinite(Number(plan.billingPeriod)) && Number(plan.billingPeriod) > 0
        ? Number(plan.billingPeriod)
        : 1;
    const normalizedUnit = ['day', 'month', 'year'].includes(plan.billingUnit)
      ? plan.billingUnit
      : 'month';

    const findMatchingProduct = () =>
      (this.productsCache || []).find((product) => {
        const descriptionMatch =
          product.description && (plan.productDescription || plan.description)
            ? product.description.trim().toLowerCase() ===
              (plan.productDescription || plan.description).trim().toLowerCase()
            : false;
        const priceMatch =
          Number(product.price || 0).toFixed(2) === normalizedPriceFixed;
        const periodMatch = Number(product.period || 0) === normalizedPeriod;
        const unitMatch = (product.periodUnit || 'month') === normalizedUnit;
        return descriptionMatch || (priceMatch && periodMatch && unitMatch);
      });

    let product = findMatchingProduct();

    if (product && product.isActive === false) {
      try {
        const updated = await adminApi.updateProduct(product._id || product.id, { isActive: true });
        product = (updated && updated.data) ? updated.data : updated;
        const index = this.productsCache.findIndex((item) => (item._id || item.id) === (product._id || product.id));
        if (index >= 0) {
          this.productsCache[index] = product;
        }
      } catch (error) {
        // Ignore activation error; proceed with existing product reference.
      }
    }

    if (!product) {
      const createPayload = {
        description: plan.productDescription || plan.description || plan.name,
        price: normalizedPrice,
        period: normalizedPeriod,
        periodUnit: normalizedUnit,
      };
      const created = await adminApi.createProduct(createPayload);
      product = (created && created.data) ? created.data : created;
      if (product) {
        if (!this.productsCache) {
          this.productsCache = [];
        }
        this.productsCache.push(product);
      }
    }

    if (!product || !(product._id || product.id)) {
      throw new Error('Unable to locate or create a product for this plan.');
    }

    const productId = product._id || product.id;
    if (
      !productId ||
      typeof productId !== 'string' ||
      !/^[a-f0-9]{24}$/i.test(productId)
    ) {
      throw new Error(`Invalid product identifier resolved for this plan.`);
    }
    if (planKey) {
      this.planProductMap.set(planKey, productId);
    }
    return productId;
  }

  notifyCustomersRefresh() {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('asi:admin-customers-refresh'));
      try {
        window.localStorage.setItem('asi:admin-customers-refresh-ts', String(Date.now()));
      } catch (error) {
        // Ignore storage errors.
      }
    }
  }

  handleApproveRequest = async (requestId) => {
    const { admin } = this.props;
    const adminUser = admin && admin.user;
    if (!adminUser || adminUser.type !== 'superadmin') {
      message.error('Only superadmins can approve payment requests.');
      return;
    }

    const { subscriptionRequests } = this.state;
    const request = subscriptionRequests.find((item) => item.id === requestId);
    if (!request) {
      message.warning('Unable to find that request. Try refreshing the page.');
      return;
    }

    const catalogPlan = getPlanById(request.planId);
    const publishedPlan =
      findPublishedPlan(request.planId) ||
      (request.productId ? findPublishedPlan(request.productId) : null);

    const plan = publishedPlan || catalogPlan || {
      planId: request.planId,
      id: request.planId,
      productId: request.productId || null,
      name: request.planName || 'Plan',
      description: request.planName || 'Plan',
      priceAmount: request.planPriceAmount || 0,
      priceDisplay: request.planPriceDisplay || null,
      billingPeriod: request.planBillingPeriod || 1,
      billingUnit: request.planBillingUnit || 'month',
      features: Array.isArray(request.planFeatures) ? request.planFeatures : [],
    };

    const isBackendUserId =
      typeof request.userId === 'string' && /^[a-f0-9]{24}$/i.test(request.userId);

    this.setState({ approvingRequestId: requestId });
    try {
      let productId = null;
      let assignData = null;
      if (isBackendUserId) {
        productId = await this.ensureProductForPlan(plan);
        const response = await adminApi.assignSubscription({
          userId: request.userId,
          productId,
          startsAt: new Date().toISOString(),
        });
        assignData = response && response.data ? response.data : response;
      } else {
        // eslint-disable-next-line no-console
        console.info(
          '[AdminDashboard] Skipping backend subscription assignment for mock user id',
          request.userId,
        );
        await this.ensureProductForPlan(plan);
      }
      let approvedRequest = null;
      const statusResponse = await adminApi.updateSubscriptionRequest(requestId, { status: 'approved' });
      const statusPayload = statusResponse && statusResponse.data ? statusResponse.data : {};
      approvedRequest = statusPayload.data || request;
      if (
        assignData &&
        assignData.subscription &&
        assignData.subscription.status === 'active' &&
        assignData.active &&
        request.userId
      ) {
        try {
          const activeSubscription = assignData.active;
          const activeProduct = activeSubscription.product || {};
          const resolvedPlanId = plan.planId || plan.id || request.planId || activeProduct.planId || null;
          const resolvedPlanName = plan.name || request.planName || activeProduct.description || 'Plan';
          const productIdentifier =
            (activeProduct && (activeProduct._id || activeProduct.id)) ||
            productId ||
            request.productId ||
            plan.productId ||
            null;
          const productPrice =
            activeProduct && activeProduct.price !== undefined
              ? Number(activeProduct.price)
              : plan.priceAmount !== undefined && plan.priceAmount !== null
              ? Number(plan.priceAmount)
              : request.planPriceAmount !== undefined && request.planPriceAmount !== null
              ? Number(request.planPriceAmount)
              : null;
          const billingPeriod =
            activeProduct && activeProduct.period !== undefined
              ? Number(activeProduct.period)
              : plan.billingPeriod !== undefined && plan.billingPeriod !== null
              ? Number(plan.billingPeriod)
              : request.planBillingPeriod !== undefined && request.planBillingPeriod !== null
              ? Number(request.planBillingPeriod)
              : null;
          const billingUnit =
            (activeProduct && activeProduct.periodUnit) ||
            plan.billingUnit ||
            request.planBillingUnit ||
            null;
          const priceDisplay = formatMonthlyPrice(
            productPrice != null ? productPrice : request.planPriceAmount,
            billingPeriod != null ? billingPeriod : request.planBillingPeriod,
            billingUnit || request.planBillingUnit,
          );

          persistApprovedSubscription(request.userId, {
            planId: resolvedPlanId,
            planName: resolvedPlanName,
            productId: productIdentifier,
            approvedAt: activeSubscription.startsAt || new Date().toISOString(),
            planPriceAmount: productPrice,
            planPriceDisplay: priceDisplay,
            planBillingPeriod: billingPeriod,
            planBillingUnit: billingUnit,
            planFeatures: Array.isArray(plan.features)
              ? plan.features
              : Array.isArray(request.planFeatures)
              ? request.planFeatures
              : [],
            userName: (approvedRequest && approvedRequest.userName) || request.userName || null,
            userEmail: (approvedRequest && approvedRequest.userEmail) || request.userEmail || null,
            userCompany: request.userCompany || null,
          });
        } catch (persistError) {
          // eslint-disable-next-line no-console
          console.debug('[AdminDashboard] Failed to persist approved subscription locally', persistError);
        }
      }
      if (approvedRequest) {
        message.success(
          `Approved the ${plan.name} plan for ${approvedRequest.userName || 'the user'}.`,
        );
      }
      this.notifyCustomersRefresh();
      this.fetchSubscriptionRequests();
      this.fetchOverview();
    } catch (error) {
      // Surface detailed info in the console for debugging.
      // eslint-disable-next-line no-console
      console.error('Failed to approve credit plan request', error);
      const errorMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : (error && error.message) || 'Failed to approve the credit plan request.';
      message.error(errorMessage);
    } finally {
      this.setState({ approvingRequestId: null });
    }
  };

  renderSubscriptionRequests() {
    const { subscriptionRequests, approvingRequestId } = this.state;
    const { admin } = this.props;
    const adminUser = admin && admin.user;
    const isSuperAdmin = adminUser && adminUser.type === 'superadmin';
    const isViewer = adminUser && adminUser.type === 'viewer';
    const pending = subscriptionRequests
      .filter((request) => request.status === 'pending')
      .sort((a, b) => {
        const aTime = new Date(a.submittedAt || 0).getTime();
        const bTime = new Date(b.submittedAt || 0).getTime();
        return bTime - aTime;
      });

    if (!pending.length) {
      return <p className="admin-dashboard__empty">No credit plan requests awaiting approval.</p>;
    }

    const columns = [
      {
        title: 'User',
        dataIndex: 'userName',
        key: 'userName',
        render: (value, record) => value || record.userId,
      },
      {
        title: 'Requested plan',
        dataIndex: 'planName',
        key: 'planName',
        render: (value, record) => value || record.planId,
      },
      {
        title: 'Submitted',
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        render: (value) => (value ? new Date(value).toLocaleString() : '—'),
      },
      ...(!isViewer ? [{
        title: 'Actions',
        key: 'actions',
        render: (value, record) => {
          if (!isSuperAdmin) {
            return <span>Superadmin approval required</span>;
          }
          return (
            <button
              type="button"
              className="admin-dashboard__request-approve"
              disabled={approvingRequestId === record.id}
              onClick={() => this.handleApproveRequest(record.id)}
            >
              {approvingRequestId === record.id ? 'Approving…' : 'Approve'}
            </button>
          );
        },
      }] : []),
    ];

    return (
      <Table
        className="admin-dashboard__table"
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={pending}
        pagination={false}
        size="small"
      />
    );
  }

  renderUsageTable() {
    const { overview } = this.state;
    if (!overview || !overview.usage || !overview.usage.length) {
      return <p className="admin-dashboard__empty">No API activity recorded in the last 24 hours.</p>;
    }

    const columns = [
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        render: (value) => (value === 'imagine' ? 'Image generation' : 'Image describe'),
      },
      {
        title: 'Total calls',
        dataIndex: 'total',
        key: 'total',
      },
      {
        title: 'Successful',
        dataIndex: 'success',
        key: 'success',
      },
    ];

    return (
      <Table
        className="admin-dashboard__table"
        rowKey={(record) => record.action}
        columns={columns}
        dataSource={overview.usage}
        pagination={false}
        size="small"
      />
    );
  }

  renderUsageSummary() {
    const { overview } = this.state;
    const totals = overview && overview.usageSummary;
    if (!totals) {
      return <p className="admin-dashboard__empty">No usage summary available.</p>;
    }

    const successRate = totals.successRate ? `${totals.successRate.toFixed(1)}%` : '0%';

    return (
      <ul className="admin-dashboard__summary-list">
        <li>
          <span>Total calls</span>
          <strong>{totals.total || 0}</strong>
        </li>
        <li>
          <span>Successful calls</span>
          <strong>{totals.success || 0}</strong>
        </li>
        <li>
          <span>Success rate</span>
          <strong>{successRate}</strong>
        </li>
      </ul>
    );
  }

  renderMonthlyUsageList() {
    const { overview } = this.state;
    const monthlyUsage = overview && overview.monthlyUsage;
    if (!monthlyUsage || !monthlyUsage.length) {
      return <p className="admin-dashboard__empty">No monthly usage data yet.</p>;
    }

    return (
      <ul className="admin-dashboard__usage-list">
        {monthlyUsage.slice(0, 6).map((item) => {
          const year = item.year !== undefined ? item.year : (item._id ? item._id.year : 0);
          const monthValue = item.month !== undefined ? item.month : (item._id ? item._id.month : 0);
          const action = item.action || (item._id ? item._id.action : 'unknown');
          const month = String(monthValue || 0).padStart(2, '0');

          return (
            <li key={`${year}-${month}-${action}`}>
              <span>{`${year}/${month} – ${action}`}</span>
              <strong>{item.total}</strong>
            </li>
          );
        })}
      </ul>
    );
  }

  renderMonthlyRevenueList() {
    const { overview } = this.state;
    const monthlyRevenue = overview && overview.revenue ? overview.revenue.monthlyRevenue : null;
    if (!monthlyRevenue || !monthlyRevenue.length) {
      return <p className="admin-dashboard__empty">No revenue data yet.</p>;
    }

    return (
      <ul className="admin-dashboard__usage-list">
        {monthlyRevenue.slice(0, 6).map((item) => {
          const month = String(item.month || 0).padStart(2, '0');
          return (
            <li key={`${item.year}-${month}`}>
              <span>{`${item.year}/${month}`}</span>
              <strong>{formatCurrencyAmount(item.revenue || 0, { minimumFractionDigits: 2 })}</strong>
            </li>
          );
        })}
      </ul>
    );
  }

  render() {
    const { loading, overview, error } = this.state;
    const { admin, onLogout } = this.props;

    return (
      <AdminLayout
        title="Admin Overview"
        subtitle="Monitor platform activity and manage user access."
        activeKey="dashboard"
        admin={admin}
        onLogout={onLogout}
      >
        {loading && (
          <div className="admin-dashboard__loading">
            <Spin />
          </div>
        )}
        {!loading && error && (
          <Card>
            <p>{error}</p>
            <button type="button" className="admin-dashboard__retry" onClick={() => this.fetchOverview()}>
              Retry
            </button>
          </Card>
        )}
        {!loading && !error && overview && (
          <div className="admin-dashboard">
            <section className="admin-dashboard__section">
              <header className="admin-dashboard__section-header">
                <h2>Platform Snapshot</h2>
                <p>Key user, product, and revenue insights at a glance.</p>
              </header>
              <div className="admin-dashboard__metrics-grid">
                <Card className="admin-dashboard__card admin-dashboard__card--metric" bordered={false}>
                  <h3 className="admin-dashboard__card-title">Users</h3>
                  <div className="admin-dashboard__stat-grid">
                    <Statistic title="Total users" value={overview.users ? overview.users.total : 0} />
                    <Statistic title="Verified" value={overview.users ? overview.users.verified : 0} />
                    <Statistic title="New (24h)" value={overview.users ? overview.users.newIn24h : 0} />
                  </div>
                </Card>
                <Card className="admin-dashboard__card admin-dashboard__card--metric" bordered={false}>
                  <h3 className="admin-dashboard__card-title">Products & Plans</h3>
                  <div className="admin-dashboard__stat-grid">
                    <Statistic title="Catalog size" value={overview.products ? overview.products.total : 0} />
                    <Statistic title="Active plans" value={overview.products ? overview.products.active : 0} />
                    <Statistic title="Active credit plans" value={overview.revenue ? overview.revenue.activeSubscriptions : 0} />
                  </div>
                </Card>
                <Card className="admin-dashboard__card admin-dashboard__card--metric" bordered={false}>
                  <h3 className="admin-dashboard__card-title">Revenue</h3>
                  <div className="admin-dashboard__stat-grid">
                    <Statistic title="Monthly recurring" prefix="฿" precision={2} value={overview.revenue ? overview.revenue.activeMonthlyRevenue : 0} />
                    <Statistic title="Lifetime" prefix="฿" precision={2} value={overview.revenue ? overview.revenue.lifetimeRevenue : 0} />
                    <Statistic title="Renewals (7 days)" value={overview.revenue ? overview.revenue.renewalsIn7Days : 0} />
                  </div>
                </Card>
              </div>
            </section>

            <section className="admin-dashboard__section">
              <header className="admin-dashboard__section-header">
                <h2>Usage Insights</h2>
                <p>Monitor API consumption trends and service health.</p>
              </header>
              <div className="admin-dashboard__grid">
                <Card className="admin-dashboard__card" title="API usage (last 24h)" bordered={false}>
                  {this.renderUsageTable()}
                </Card>
                <Card className="admin-dashboard__card" title="Usage health (24h)" bordered={false}>
                  {this.renderUsageSummary()}
                </Card>
                <Card className="admin-dashboard__card" title="Monthly API usage" bordered={false}>
                  {this.renderMonthlyUsageList()}
                </Card>
                <Card className="admin-dashboard__card" title="Monthly revenue" bordered={false}>
                  {this.renderMonthlyRevenueList()}
                </Card>
              </div>
            </section>

            <section className="admin-dashboard__section">
              <header className="admin-dashboard__section-header">
                <h2>Credit Plan Requests</h2>
                <p>Review plan change requests submitted by users.</p>
              </header>
              <Card className="admin-dashboard__card" bordered={false}>
                {this.renderSubscriptionRequests()}
              </Card>
            </section>
          </div>
        )}
      </AdminLayout>
    );
  }
}

AdminDashboard.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
  onLogout: PropTypes.func.isRequired,
};

AdminDashboard.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminDashboard);
